import React, { useState, useEffect, useRef, useCallback } from "react";
import Chart from "chart.js/auto";
import "./CAS3.css";

interface CAS3Props {
  isUnlocked?: boolean;
  onChatbotTrigger?: (visible: boolean) => void;
}

type ScenarioKey = "conservative" | "base" | "best";

const SCENARIOS: Record<
  ScenarioKey,
  {
    sessionsPerDay: number;
    downtimeDays: number;
    utilizationPct: number;
    color: string;
    label: string;
  }
> = {
  conservative: {
    sessionsPerDay: 5,
    downtimeDays: 37,
    utilizationPct: 21,
    color: "#666",
    label: "Conservative",
  },
  base: {
    sessionsPerDay: 10,
    downtimeDays: 18,
    utilizationPct: 42,
    color: "#E8544F",
    label: "Base Case",
  },
  best: {
    sessionsPerDay: 16,
    downtimeDays: 12,
    utilizationPct: 67,
    color: "#4CAF50",
    label: "Best Case",
  },
};

const YEARS = ["2027", "2028", "2029", "2030", "2031"];
const POD_COUNTS = [1, 20, 60, 240, 400];

function calculateRevenue(
  pods: number,
  sessionsPerDay: number,
  downtimeDays: number
): number {
  const operatingDays = 365 - downtimeDays;
  return pods * sessionsPerDay * operatingDays * 20;
}

function formatCurrency(value: number): string {
  if (value >= 1000000) return "£" + (value / 1000000).toFixed(2) + "M";
  if (value >= 1000) return "£" + (value / 1000).toFixed(0) + "K";
  return "£" + value.toFixed(0);
}

function getRevenueData(scenario: ScenarioKey): number[] {
  const config = SCENARIOS[scenario];
  return POD_COUNTS.map((pods) =>
    calculateRevenue(pods, config.sessionsPerDay, config.downtimeDays)
  );
}

const CAS3: React.FC<CAS3Props> = ({ isUnlocked = true, onChatbotTrigger }) => {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(
    new Set()
  );
  const [activeScenario, setActiveScenario] = useState<ScenarioKey>("base");
  const [animatedValues, setAnimatedValues] = useState({
    revenue: "£0",
    pods: "0",
    util: "0%",
    sessions: "0",
  });
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLElement>(null);
  const revenueCanvasRef = useRef<HTMLCanvasElement>(null);
  const sitesCanvasRef = useRef<HTMLCanvasElement>(null);
  const revenueChartRef = useRef<Chart | null>(null);
  const sitesChartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!isUnlocked) return;
    setVisibleSections(
      new Set([
        "setup",
        "category",
        "machine",
        "model",
        "tailwinds",
        "growth",
        "close",
      ])
    );
  }, [isUnlocked]);

  useEffect(() => {
    if (!onChatbotTrigger) return;
    const caLane = document.querySelector(".ca-lane");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => onChatbotTrigger(entry.isIntersecting));
      },
      { threshold: 0.3, root: caLane }
    );
    if (closeRef.current) observer.observe(closeRef.current);
    return () => observer.disconnect();
  }, [onChatbotTrigger]);

  const getChartOptions = useCallback(
    (isCurrency: boolean): any => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1.8,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1a1a1a",
          titleColor: "#f5f5dc",
          bodyColor: "#f5f5dc",
          borderColor: "#E8544F",
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          callbacks: {
            label: (context: any) =>
              isCurrency
                ? formatCurrency(context.parsed.y)
                : context.parsed.y + " pods",
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "#252525", drawBorder: false },
          ticks: {
            color: "#888",
            callback: (value: number) =>
              isCurrency ? formatCurrency(value) : value,
          },
        },
        x: {
          grid: { display: false, drawBorder: false },
          ticks: { color: "#888" },
        },
      },
      elements: {
        line: { tension: 0.4, borderWidth: 3 },
        point: {
          radius: 5,
          hoverRadius: 7,
          backgroundColor: "#0a0a0a",
          borderWidth: 2,
        },
      },
    }),
    []
  );

  useEffect(() => {
    if (!isUnlocked) return;
    if (!revenueCanvasRef.current || !sitesCanvasRef.current) return;

    const timer = setTimeout(() => {
      if (revenueChartRef.current) revenueChartRef.current.destroy();
      if (sitesChartRef.current) sitesChartRef.current.destroy();

      const color = SCENARIOS[activeScenario].color;

      revenueChartRef.current = new Chart(revenueCanvasRef.current!, {
        type: "line",
        data: {
          labels: YEARS,
          datasets: [
            {
              label: "Revenue",
              data: getRevenueData(activeScenario),
              borderColor: color,
              pointBorderColor: color,
            } as any,
          ],
        },
        options: getChartOptions(true),
      });

      sitesChartRef.current = new Chart(sitesCanvasRef.current!, {
        type: "line",
        data: {
          labels: YEARS,
          datasets: [
            {
              label: "Sites",
              data: POD_COUNTS,
              borderColor: color,
              pointBorderColor: color,
            } as any,
          ],
        },
        options: getChartOptions(false),
      });
    }, 300);

    return () => {
      clearTimeout(timer);
      revenueChartRef.current?.destroy();
      sitesChartRef.current?.destroy();
    };
  }, [isUnlocked, getChartOptions]);

  useEffect(() => {
    const color = SCENARIOS[activeScenario].color;

    if (revenueChartRef.current) {
      revenueChartRef.current.data.datasets[0].data =
        getRevenueData(activeScenario);
      revenueChartRef.current.data.datasets[0].borderColor = color;
      (revenueChartRef.current.data.datasets[0] as any).pointBorderColor =
        color;
      revenueChartRef.current.update("active");
    }

    if (sitesChartRef.current) {
      sitesChartRef.current.data.datasets[0].borderColor = color;
      (sitesChartRef.current.data.datasets[0] as any).pointBorderColor = color;
      sitesChartRef.current.update("active");
    }

    const config = SCENARIOS[activeScenario];
    const targetRevenue = getRevenueData(activeScenario)[4];
    const duration = 1200;
    const steps = 40;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedValues({
        revenue: formatCurrency(Math.round(targetRevenue * eased)),
        pods: String(Math.round(400 * eased)),
        util: Math.round(config.utilizationPct * eased) + "%",
        sessions: String(Math.round(config.sessionsPerDay * eased)),
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    return () => clearInterval(timer);
  }, [activeScenario]);

  if (!isUnlocked) {
    return (
      <div className="cas3-locked">
        <div className="lock-overlay">
          <div className="lock-content">
            <div className="lock-icon">🔒</div>
            <p>Complete the previous sections to unlock</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cas3-container" ref={containerRef}>
      {/* Beat 01 — The Setup */}
      <section className="cas3-section cas3-setup" data-section-id="setup">
        <div className="setup-bg setup-bg-1" />
        <div className="setup-bg setup-bg-2" />
        <div className="setup-overlay" />
        <div
          className={`cas3-content ${
            visibleSections.has("setup") ? "visible" : ""
          }`}
        >
          <div className="beat-number">01</div>
          <h1>
            You've seen what it does.
            <br />
            <span className="coral">Now see where it goes.</span>
          </h1>
          <p className="setup-sub">
            Cities have gyms, cafés, bars. Infrastructure for energy,
            productivity, socialising. But nothing - nothing - for nervous
            system recovery. Not a single category exists.
          </p>
          <p className="setup-accent">
            That's not a gap in the market. That's a missing layer of
            infrastructure.
          </p>
        </div>
      </section>

      {/* Beat 02 — The Category */}
      <section
        className="cas3-section cas3-category"
        data-section-id="category"
      >
        <div
          className={`cas3-content ${
            visibleSections.has("category") ? "visible" : ""
          }`}
        >
          <div className="beat-number">02</div>
          <div className="category-label">THE CATEGORY WE'RE CREATING</div>
          <h2>Urban decompression infrastructure.</h2>
          <div className="timeline-comparison">
            <div className="timeline-item">
              <div className="timeline-year">1970s</div>
              <div className="timeline-text">
                Gyms didn't exist as a consumer category
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2010s</div>
              <div className="timeline-text">
                Co-working spaces didn't exist as an asset class
              </div>
            </div>
            <div className="timeline-item active">
              <div className="timeline-year">2027</div>
              <div className="timeline-text">
                Urban reset infrastructure doesn't exist.{" "}
                <span className="coral">Yet.</span>
              </div>
            </div>
          </div>
          <p className="category-closer">
            The wellness experience is the product. The platform is the
            business. LOA is the operating system for a category that's
            inevitable.
          </p>
        </div>
      </section>

      {/* Beat 03 — The Product as a Machine */}
      <section className="cas3-section cas3-machine" data-section-id="machine">
        <div
          className={`cas3-content ${
            visibleSections.has("machine") ? "visible" : ""
          }`}
        >
          <div className="beat-number">03</div>
          <div className="machine-label">THE PRODUCT AS A REVENUE UNIT</div>
          <h2>One pod. One machine.</h2>

          <div className="unit-economics">
            <div className="unit-card">
              <div className="unit-value">50</div>
              <div className="unit-metric">sqft footprint</div>
            </div>
            <div className="unit-card">
              <div className="unit-value">24</div>
              <div className="unit-metric">slots per day</div>
            </div>
            <div className="unit-card">
              <div className="unit-value">£20</div>
              <div className="unit-metric">per session</div>
            </div>
            <div className="unit-card highlight">
              <div className="unit-value">6–12</div>
              <div className="unit-metric">month payback</div>
            </div>
          </div>

          <div className="dual-strategy">
            <div className="strategy-card">
              <div className="strategy-label">BREATHE POD 50</div>
              <p>The scalable unit. Deploy anywhere. Revenue engine.</p>
            </div>
            <div className="strategy-divider">
              <span>funds</span>
              <div className="divider-arrow">→</div>
            </div>
            <div className="strategy-card mission">
              <div className="strategy-label">BREATHE BAR 200</div>
              <p>
                The mission product. Kids zone, social space, community impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beat 04 — The Business Model */}
      <section className="cas3-section cas3-model" data-section-id="model">
        <div
          className={`cas3-content ${
            visibleSections.has("model") ? "visible" : ""
          }`}
        >
          <div className="beat-number">04</div>
          <div className="model-label">THE BUSINESS MODEL</div>
          <h2>Two platforms. One asset-light model.</h2>

          <div className="model-split">
            <div className="model-column">
              <div className="model-column-header">SPACE OS</div>
              <ul>
                <li>Operator-facing platform</li>
                <li>Autonomous pod management</li>
                <li>Access control & scheduling</li>
                <li>Landlord reporting & revenue share</li>
              </ul>
            </div>
            <div className="model-arrow">
              <div className="arrow-line"></div>
              <div className="arrow-center">Shared Data</div>
              <div className="arrow-line"></div>
            </div>
            <div className="model-column">
              <div className="model-column-header">MEMBER APP</div>
              <ul>
                <li>Consumer-facing experience</li>
                <li>Booking & digital access</li>
                <li>Mood tracking & biometrics</li>
                <li>Membership & rewards</li>
              </ul>
            </div>
          </div>

          <div className="model-highlights">
            <div className="model-highlight">
              <span className="highlight-icon">⚡</span>
              <span>Fully autonomous - zero staff required at location</span>
            </div>
            <div className="model-highlight">
              <span className="highlight-icon">📈</span>
              <span>
                Platform revenue via digital access and recurring membership
              </span>
            </div>
            <div className="model-highlight">
              <span className="highlight-icon">🏗️</span>
              <span>Landlord funds fit-out - no capex risk for LOA</span>
            </div>
          </div>
        </div>
      </section>

      {/* Beat 05 — Market Timing / Tailwinds */}
      <section
        className="cas3-section cas3-tailwinds"
        data-section-id="tailwinds"
      >
        <div
          className={`cas3-content ${
            visibleSections.has("tailwinds") ? "visible" : ""
          }`}
        >
          <div className="beat-number">05</div>
          <div className="tailwinds-label">WHY NOW</div>
          <h2>The window is open.</h2>
          <p className="tailwinds-hint">Tap a card to flip</p>

          <div className="tailwinds-grid">
            {[
              {
                icon: "🏗️",
                title: "Wellness Real Estate",
                stat: "$548bn",
                front:
                  "Growing 4x faster than construction. UK leading at 29% annual growth. Projected $1.1tn by 2029.",
                back: "The Global Wellness Institute reports wellness real estate doubled from $225bn to $548bn between 2019–2024, at 19.5% annually vs 5.5% for overall construction. The UK is the fastest-growing national market. But developers are building wellness into buildings - gyms, air purification, biophilic design. Nobody is building standalone, deployable wellness infrastructure between buildings. No turnkey solution exists for landlords who want to add wellness amenity without major capex.",
              },
              {
                icon: "📦",
                title: "Micro-Formats",
                stat: "$81m+",
                front:
                  "Marriott just acquired Postcard Cabins. Unyoked raised $35m. The majors are moving.",
                back: "In December 2024, Marriott acquired Postcard Cabins (formerly Getaway) - 1,200 tiny cabins, $41m annual revenue, $81m venture funding - to launch an outdoor-focused collection. Unyoked raised $35m for off-grid cabins across Australia, NZ and Europe. Sleep pods in airports, phone booths in offices, meditation rooms in co-working - all proving demand. But every one of these is rural or travel-based. Nobody has brought the micro-decompression format into the city centre itself.",
              },
              {
                icon: "🏢",
                title: "Urban Vacancies",
                stat: "Rising",
                front:
                  "UK high street vacancy rates climbing. London office vacancy at a 20-year high. Landlords need activation models, not traditional tenants.",
                back: "Centre for Cities data shows UK high street vacancy rates ranging from 7% to over 19% depending on location, with persistently empty sub-200sqft units among the hardest to let. London office vacancy hit 10.6% in 2024 - a 20-year high. The government is calling for billions to repurpose excess retail space. Landlords with secondary space need anything that adds value without full refurb. A pod on a management agreement - no capex for the landlord, revenue share, zero operational burden - is the model the market is waiting for.",
              },
              {
                icon: "🧠",
                title: "Generational Shift",
                stat: "Gen Z+",
                front:
                  "Wellness isn't a luxury for younger generations. It's a hygiene factor. Non-negotiable.",
                back: "For Gen Z and younger millennials, mental health and wellbeing aren't aspirational - they're baseline expectations. They demand it in their workplaces, their housing, their cities. This shift is consistently evidenced across occupier surveys, workplace design reports, and consumer research. This generation won't wait for permission to prioritise their nervous system. They expect infrastructure that supports it. LOA isn't selling wellness. It's building what they already consider essential.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`tailwind-flip-container ${
                  flippedCards.has(i) ? "flipped" : ""
                }`}
                onClick={() => {
                  setFlippedCards((prev) => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    return next;
                  });
                }}
              >
                <div className="tailwind-flip-inner">
                  <div className="tailwind-front">
                    <div className="tailwind-icon">{card.icon}</div>
                    <div className="tailwind-stat">{card.stat}</div>
                    <h3>{card.title}</h3>
                    <p>{card.front}</p>
                  </div>
                  <div className="tailwind-back">
                    <p>{card.back}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beat 06 — The Growth Model */}
      <section className="cas3-section cas3-growth" data-section-id="growth">
        <div
          className={`cas3-content growth-wide ${
            visibleSections.has("growth") ? "visible" : ""
          }`}
        >
          <div className="beat-number">06</div>
          <h2>5-Year Growth Model</h2>
          <p className="growth-sub">
            Toggle between scenarios to explore different trajectories
          </p>

          <div className="scenario-toggle">
            {(Object.keys(SCENARIOS) as ScenarioKey[]).map((key) => (
              <button
                key={key}
                className={`scenario-btn ${
                  activeScenario === key ? "active" : ""
                }`}
                onClick={() => setActiveScenario(key)}
              >
                {SCENARIOS[key].label}
              </button>
            ))}
          </div>

          <div className="chart-container">
            <canvas ref={revenueCanvasRef}></canvas>
          </div>

          <div className="chart-container">
            <canvas ref={sitesCanvasRef}></canvas>
          </div>

          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Year 5 Revenue</div>
              <div className="metric-value">{animatedValues.revenue}</div>
              <div className="metric-sub">Annual gross revenue</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Year 5 Sites</div>
              <div className="metric-value">{animatedValues.pods}</div>
              <div className="metric-sub">Deployed pods</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Utilisation</div>
              <div className="metric-value">{animatedValues.util}</div>
              <div className="metric-sub">Average slot fill rate</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Sessions/Day</div>
              <div className="metric-value">{animatedValues.sessions}</div>
              <div className="metric-sub">Per pod average</div>
            </div>
          </div>

          <div className="assumptions">
            <div className="assumptions-header">Model Assumptions</div>
            <ul className="assumptions-list">
              <li>
                <strong>Operating Hours:</strong> 7am–7pm daily (24 slots ×
                30min)
              </li>
              <li>
                <strong>Session Price:</strong> £20 per 20-minute session
              </li>
              <li>
                <strong>Growth Targets:</strong> 1 → 20 → 60 → 240 → 400 pods
                (2027–2031)
              </li>
              <li>
                <strong>Conservative:</strong> 5 sessions/day, 10% downtime (37
                days/year)
              </li>
              <li>
                <strong>Base Case:</strong> 10 sessions/day, 5% downtime (18
                days/year)
              </li>
              <li>
                <strong>Best Case:</strong> 16 sessions/day, 3% downtime (12
                days/year)
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Beat 07 — The Close */}
      <section
        className="cas3-section cas3-close"
        data-section-id="close"
        ref={closeRef}
        style={{ backgroundImage: "url(/images/IMG_5795.PNG)" }}
      >
        <div className="close-overlay" />
        <div
          className={`cas3-content cas3-close-content ${
            visibleSections.has("close") ? "visible" : ""
          }`}
        >
          <div className="close-logo">LOA</div>
          <h2>
            The city takes.
            <br />
            <span className="coral">LOA gives back.</span>
          </h2>
          <p className="close-cta">Let's build this together.</p>
        </div>
      </section>
    </div>
  );
};

export default CAS3;
